import * as React from "react";
import { CheckCircle2, RefreshCw, XCircle, type LucideIcon } from "lucide-react";
import * as styles from "./SmartLinkSyncStatusBadge.css";

export type SmartLinkSyncStatus = "in_sync" | "out_of_sync" | "unknown";

const ICON: Record<SmartLinkSyncStatus, LucideIcon> = {
  in_sync: CheckCircle2,
  out_of_sync: XCircle,
  // "Unknown" doubles as "a check is likely in flight" -- see iconChecking's
  // spin animation below -- so this uses a refresh glyph, not a static "?".
  unknown: RefreshCw,
};

const CLASS: Record<SmartLinkSyncStatus, string> = {
  in_sync: styles.badgeInSync,
  out_of_sync: styles.badgeOutOfSync,
  unknown: styles.badgeUnknown,
};

const TITLE: Record<SmartLinkSyncStatus, string> = {
  in_sync: "Synced",
  out_of_sync: "Out of sync",
  unknown: "Checking…",
};

/**
 * Reusable across the Smart Link admin list, the general Users list, and a
 * user's detail page — same three states everywhere, backed by the claim's
 * cheap stored `syncStatus` column (never a live per-render Smart fetch).
 * Unlike the email/phone verification badges this is modeled on, the label
 * stays "Smart Link" in every state — only the icon shape and color denote
 * status (a checkmark vs. an X reads clearly even when colors are similar,
 * which a plain colored dot did not). "Unknown" spins its icon, since that
 * status is exactly what the opportunistic sweep targets — the badge
 * resolves on its own via a live update once the check completes, no manual
 * refresh needed. Callers should only render this for linked users — this
 * component doesn't itself check link state.
 */
export function SmartLinkSyncStatusBadge({
  status,
  label = "Smart Link",
}: {
  status: SmartLinkSyncStatus;
  label?: string;
}) {
  const Icon = ICON[status];
  return (
    <span className={`${styles.badge} ${CLASS[status]}`} title={TITLE[status]}>
      <Icon
        size={12}
        aria-hidden
        strokeWidth={2.5}
        className={status === "unknown" ? styles.iconChecking : undefined}
      />
      {label}
    </span>
  );
}
